import * as React from "react";
import {
  createBoundary,
  createCaseLink,
  createOuterPolygons,
  createSubAreaPolygons,
  createCoverageCircle,
  createAnchorMarker,
  isBoundaryDrawable,
  updateCaseLink,
  updateSubAreaPolygons,
  updateCoverageCircle,
  type SubAreaVisualState,
} from "./overlays";
import type { BBox } from "./types";
import { boundaryFor, boundsOfMulti, type SubAreaSpec } from "./pilotBoundary";
import type { Selected } from "./useNaverMarkers";

interface UseNaverOverlaysArgs {
  map: any | null;
  bbox: BBox | null | undefined;
  links: Array<{ elderly_id: number; care_manager_id: number }> | null | undefined;
  elderlies: any[] | null | undefined;
  managers: any[] | null | undefined;
  showBoundary: boolean;
  showLinks: boolean;
  selected: Selected | null;
  // ── 파일럿 권역(김해 R018 · 남양주 R019) 전용. 경계 데이터가 없는 권역은
  //    기존 bbox rect 경계로 폴백한다(pilotBoundary.ts SOURCES 참고). ──
  regionCode?: string | null;
  selectedSubArea?: string | null;
  // 커버리지 원(복지관 앵커 반경) 표시 여부. 오버레이 토글에 연동.
  showCoverage?: boolean;
  // 지도 오른쪽을 덮는 패널(권역 관리/선택 상세) 폭. 구역 fitBounds가 이만큼을
  // 여백으로 잡아 선택한 구역이 패널 뒤로 숨지 않게 한다.
  fitRightPadding?: number;
}

function subAreaState(spec: SubAreaSpec, selectedKey: string | null | undefined): SubAreaVisualState {
  if (!selectedKey) return "default";
  return spec.key === selectedKey ? "selected" : "muted";
}

// 권역 경계 + 동선 라인 오버레이 라이프사이클.
// 파일럿 권역(경계 데이터 보유): 외곽 polygon 세트 + 하위구역 polygon 세트 +
//   구역별 coverage circle·anchor marker. selectedSubArea 변화 시 재도장 + fitBounds.
// 그 외 권역: bbox rect 1개.
export function useNaverOverlays({
  map,
  bbox,
  links,
  elderlies,
  managers,
  showBoundary,
  showLinks,
  selected,
  regionCode,
  selectedSubArea,
  showCoverage,
  fitRightPadding,
}: UseNaverOverlaysArgs): void {
  const boundaryRef = React.useRef<any>(null);                          // 폴리곤 없는 권역의 bbox rect
  const outerRef    = React.useRef<any[]>([]);                          // 시경계(외곽) polygons
  const subAreasRef = React.useRef(new Map<string, any[]>());           // key → polygons[]
  const coverageRef = React.useRef(new Map<string, any>());             // key → 복지관 커버리지
  const anchorRef   = React.useRef(new Map<string, any>());             // key → 복지관 앵커 마커
  const ltCoverageRef = React.useRef(new Map<string, any>());           // key → 장기요양 커버리지
  const ltAnchorRef   = React.useRef(new Map<string, any>());           // key → 장기요양 앵커 마커
  const linksRef    = React.useRef(new Map<string, { line: any; highlighted: boolean; dimOthers: boolean }>());
  const lastFitKeyRef = React.useRef<string | null>(null);
  // 패널 개폐만으로 지도를 다시 맞추지 않도록 ref로만 읽는다(fit effect의 dep 아님).
  const padRef = React.useRef(fitRightPadding);
  padRef.current = fitRightPadding;

  // 이 권역의 폴리곤 경계(없으면 null → bbox rect 폴백).
  const boundary  = boundaryFor(regionCode);
  const hasPolys  = boundary != null;
  // 마지막으로 그린 권역. 파일럿 → 다른 파일럿으로 갈아탈 때(예: 김해 → 남양주)
  // 폴리곤·앵커는 구역 key 로 캐시돼 있어서, 지우지 않으면 이전 권역 도형이 지도에
  // 그대로 남는다(cleanup 은 원래 "경계 없음/끄기" 경우에만 돌았다).
  const lastRegionRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!map || !window.naver?.maps) return;
    const naver = window.naver;

    // 권역이 바뀌었으면 이전 권역의 폴리곤·커버리지·앵커를 먼저 걷어낸다.
    const nextRegion = boundary?.regionCode ?? null;
    if (lastRegionRef.current !== nextRegion) {
      cleanupPilotPolys();
      lastRegionRef.current = nextRegion;
    }

    if (!hasPolys) {
      cleanupPilotPolys();
      const shouldDraw = showBoundary && isBoundaryDrawable(bbox);
      if (shouldDraw && !boundaryRef.current) {
        boundaryRef.current = createBoundary(naver, map, bbox);
      } else if (!shouldDraw && boundaryRef.current) {
        try { boundaryRef.current.setMap(null); } catch {}
        boundaryRef.current = null;
      } else if (shouldDraw && boundaryRef.current) {
        try {
          boundaryRef.current.setBounds(new naver.maps.LatLngBounds(
            new naver.maps.LatLng(bbox!.south, bbox!.west),
            new naver.maps.LatLng(bbox!.north, bbox!.east),
          ));
        } catch {}
      }
      return;
    }

    if (boundaryRef.current) {
      try { boundaryRef.current.setMap(null); } catch {}
      boundaryRef.current = null;
    }

    if (!showBoundary) {
      cleanupPilotPolys();
      return;
    }

    // 외곽 폴리곤 세트 (한번만 생성).
    if (outerRef.current.length === 0) {
      outerRef.current = createOuterPolygons(naver, map, boundary!.outer);
    }

    // 하위구역 폴리곤 + 커버리지 + 앵커.
    for (const spec of boundary!.subAreas) {
      const state = subAreaState(spec, selectedSubArea);

      let polys = subAreasRef.current.get(spec.key);
      if (!polys) {
        polys = createSubAreaPolygons(naver, map, spec.paths, spec.color, state);
        subAreasRef.current.set(spec.key, polys);
      } else {
        updateSubAreaPolygons(polys, spec.color, state);
      }

      if (showCoverage !== false && spec.anchor) {
        // ── 비장기요양 거점 (복지관) ─────────────────────────
        let circ = coverageRef.current.get(spec.key);
        if (!circ) {
          circ = createCoverageCircle(naver, map, spec.anchor!.point, spec.anchor!.coverage_m, spec.color, state);
          coverageRef.current.set(spec.key, circ);
        } else {
          updateCoverageCircle(circ, spec.color, state);
        }

        let anchor = anchorRef.current.get(spec.key);
        if (!anchor) {
          anchor = createAnchorMarker(naver, map, spec.anchor!.point, spec.anchor!.name, spec.color, "welfare");
          anchorRef.current.set(spec.key, anchor);
        }
        // 다른 권역 선택 상태에서는 앵커 마커도 숨겨 선택된 권역만 도드라지게.
        // HTML content라 opacity 설정이 안 통해 setMap 토글.
        try { anchor.setMap(state === "muted" ? null : map); } catch {}

        // ── 장기요양 거점 (사회복지법인 시설) ────────────────
        const la = spec.longterm_anchor;
        if (la) {
          let ltCirc = ltCoverageRef.current.get(spec.key);
          if (!ltCirc) {
            ltCirc = createCoverageCircle(naver, map, la.point, la.coverage_m, spec.color, state);
            ltCoverageRef.current.set(spec.key, ltCirc);
          } else {
            updateCoverageCircle(ltCirc, spec.color, state);
          }
          let ltAnchor = ltAnchorRef.current.get(spec.key);
          if (!ltAnchor) {
            ltAnchor = createAnchorMarker(naver, map, la.point, la.name, spec.color, "longterm");
            ltAnchorRef.current.set(spec.key, ltAnchor);
          }
          try { ltAnchor.setMap(state === "muted" ? null : map); } catch {}
        }
      } else {
        const circ = coverageRef.current.get(spec.key);
        if (circ) { try { circ.setMap(null); } catch {}; coverageRef.current.delete(spec.key); }
        const anchor = anchorRef.current.get(spec.key);
        if (anchor) { try { anchor.setMap(null); } catch {}; anchorRef.current.delete(spec.key); }
        const ltCirc = ltCoverageRef.current.get(spec.key);
        if (ltCirc) { try { ltCirc.setMap(null); } catch {}; ltCoverageRef.current.delete(spec.key); }
        const ltAnchor = ltAnchorRef.current.get(spec.key);
        if (ltAnchor) { try { ltAnchor.setMap(null); } catch {}; ltAnchorRef.current.delete(spec.key); }
      }
    }

    function cleanupPilotPolys() {
      for (const p of outerRef.current) { try { p.setMap(null); } catch {} }
      outerRef.current = [];
      for (const [, ps] of subAreasRef.current) {
        for (const p of ps) { try { p.setMap(null); } catch {} }
      }
      subAreasRef.current.clear();
      for (const [, c] of coverageRef.current) { try { c.setMap(null); } catch {} }
      coverageRef.current.clear();
      for (const [, m] of anchorRef.current) { try { m.setMap(null); } catch {} }
      anchorRef.current.clear();
      for (const [, c] of ltCoverageRef.current) { try { c.setMap(null); } catch {} }
      ltCoverageRef.current.clear();
      for (const [, m] of ltAnchorRef.current) { try { m.setMap(null); } catch {} }
      ltAnchorRef.current.clear();
    }
  }, [map, bbox, showBoundary, boundary, selectedSubArea, showCoverage]);

  // selectedSubArea 변경 시 그 구역으로 fitBounds.
  // 권역이 바뀌면 같은 구역명이라도 다시 맞춰야 하므로 fit 키에 권역 코드를 포함한다.
  React.useEffect(() => {
    if (!map || !boundary || !window.naver?.maps) return;
    const naver = window.naver;
    const key = `${boundary.regionCode}:${selectedSubArea || "__all__"}`;
    if (lastFitKeyRef.current === key) return;
    lastFitKeyRef.current = key;

    const target = selectedSubArea
      ? boundary.subAreas.find(s => s.key === selectedSubArea)?.paths
      : boundary.outer;
    if (!target || target.length === 0) return;

    const b = boundsOfMulti(target);
    try {
      map.fitBounds(new naver.maps.LatLngBounds(
        new naver.maps.LatLng(b.south, b.west),
        new naver.maps.LatLng(b.north, b.east),
      ), { top: 60, right: 60, bottom: 60, left: 60 });
      // 네이버 fitBounds의 margin은 줌 계산에만 반영되고 중심은 늘 bounds 중앙이라,
      // 오른쪽을 덮는 패널만큼은 투영 좌표로 직접 민다(useNaverRegions와 동일 처리).
      const pad = padRef.current ?? 0;
      if (pad > 60) {
        const proj = map.getProjection();
        const p = proj.fromCoordToOffset(map.getCenter());
        map.setCenter(proj.fromOffsetToCoord(new naver.maps.Point(p.x + (pad - 60) / 2, p.y)));
      }
    } catch {}
  }, [map, boundary, selectedSubArea]);

  // ── 동선 라인 ────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!map || !window.naver?.maps) return;
    const naver = window.naver;

    const eIndex = new Map((elderlies ?? []).map((x) => [x.id, x]));
    const mIndex = new Map((managers ?? []).map((x) => [x.id, x]));

    const desired = new Map<string, { e: any; m: any; highlighted: boolean }>();
    if (showLinks) {
      for (const l of links ?? []) {
        const e = eIndex.get(l.elderly_id);
        const m = mIndex.get(l.care_manager_id);
        if (!e || !m) continue;
        if (e.lat == null || e.lng == null || m.lat == null || m.lng == null) continue;
        const key = `${l.elderly_id}-${l.care_manager_id}`;
        const highlighted =
          (selected?.kind === "elderly" && selected.id === l.elderly_id) ||
          (selected?.kind === "manager" && selected.id === l.care_manager_id);
        desired.set(key, { e, m, highlighted });
      }
    }

    // 선택 대상이 있으면 무관 라인은 훨씬 흐리게 — 선택된 케이스의 동선이
    // 명확히 도드라지도록. 선택 해제 시 원래 톤으로 복귀.
    const hasSelection = !!selected;

    const current = linksRef.current;
    for (const [key, entry] of current) {
      if (!desired.has(key)) {
        try { entry.line.setMap(null); } catch {}
        current.delete(key);
      }
    }
    for (const [key, spec] of desired) {
      const existing = current.get(key);
      if (!existing) {
        const line = createCaseLink(naver, map, spec.e, spec.m, spec.highlighted, hasSelection);
        current.set(key, { line, highlighted: spec.highlighted, dimOthers: hasSelection });
      } else if (existing.highlighted !== spec.highlighted || existing.dimOthers !== hasSelection) {
        updateCaseLink(existing.line, spec.highlighted, hasSelection);
        existing.highlighted = spec.highlighted;
        existing.dimOthers   = hasSelection;
      }
    }
  }, [map, links, elderlies, managers, showLinks, selected]);

  React.useEffect(() => {
    return () => {
      if (boundaryRef.current) { try { boundaryRef.current.setMap(null); } catch {}; boundaryRef.current = null; }
      for (const p of outerRef.current) { try { p.setMap(null); } catch {} }
      outerRef.current = [];
      for (const [, ps] of subAreasRef.current) {
        for (const p of ps) { try { p.setMap(null); } catch {} }
      }
      subAreasRef.current.clear();
      for (const [, c] of coverageRef.current) { try { c.setMap(null); } catch {} }
      coverageRef.current.clear();
      for (const [, m] of anchorRef.current) { try { m.setMap(null); } catch {} }
      anchorRef.current.clear();
      for (const [, c] of ltCoverageRef.current) { try { c.setMap(null); } catch {} }
      ltCoverageRef.current.clear();
      for (const [, m] of ltAnchorRef.current) { try { m.setMap(null); } catch {} }
      ltAnchorRef.current.clear();
      for (const [, { line }] of linksRef.current) { try { line.setMap(null); } catch {} }
      linksRef.current.clear();
    };
  }, []);
}
